import React from 'react';
import { Link, NavLink, Route, Switch, withRouter, RouteComponentProps } from 'react-router-dom';

import { EditFillInBlank } from './editFillInBlank';
import { BlankByBlankGradingPanel } from './blankByBlankGradingPanel';

import { Cues } from '../renderForQuestions/cues';
import { MassEditContainer } from '../questions/massEditContainer';
import { Modal } from '../modal/modal';
import { serializeCues } from '../../index';

const icon = `${window.postbuildEnv.CDN_URL}/images/icons/direction.svg`;

interface FillInBlankQuestionOwnProps {
  ResponseComponent: React.ComponentType<any>;
  TestComponent: (props: any) => React.ReactElement;
  actions: {
    clearDisplayMessage: () => void;
    clearResponses: () => void;
    deleteAll: (questionID: string, responses: any[]) => void;
    removeResponse: (responseKey: string) => void;
    submitConceptResults: (questionID: string, responses: any[]) => void;
    submitFeedback: (questionID: string, responses: any[]) => void;
  };
  basePath: string;
  dispatch: any;
  displayError?: string;
  displayMessage?: string;
  editQuestionAction?: (questionID: string, data: any) => void;
  hasReceivedData: boolean;
  editBlankByBlankGrading?: (questionID: string, data: any) => void;
  isGrammar?: boolean;
  massEdit: {
    numSelectedResponses: number;
    selectedResponses: any[];
  };
  question: any;
  questionID: string;
  useRelativePaths?: boolean;
}

type FillInBlankQuestionProps = FillInBlankQuestionOwnProps & RouteComponentProps;

export const FillInBlankQuestionComponent: React.FC<FillInBlankQuestionProps> = ({
  ResponseComponent,
  TestComponent,
  actions,
  basePath,
  dispatch,
  displayError,
  displayMessage,
  editQuestionAction,
  hasReceivedData,
  isGrammar,
  massEdit,
  match,
  question,
  questionID,
  useRelativePaths = false,
  editBlankByBlankGrading,
}) => {
  if (!hasReceivedData) {
    return <p>Loading...</p>;
  }

  if (!question) {
    return <p>404: No Question Found</p>;
  }

  const baseUrl = useRelativePaths ? match.url : `/admin/${basePath}/${questionID}`;
  const baseRoutePath = useRelativePaths ? match.path : `/admin/${basePath}/:questionID`;

  const editPath = `${baseUrl}/edit`;
  const responsesPath = `${baseUrl}/responses`;
  const testPath = `${baseUrl}/test`;
  const massEditPath = `${baseUrl}/mass-edit`;
  const gradingPath = `${baseUrl}/grading`

  const editRoutePath = `${baseRoutePath}/edit`;
  const responsesRoutePath = `${baseRoutePath}/responses`;
  const testRoutePath = `${baseRoutePath}/test`;
  const massEditRoutePath = `${baseRoutePath}/mass-edit`;
  const gradingRoutePath = `${baseRoutePath}/grading`;

  const activeLink = massEdit.numSelectedResponses > 1
    ? <NavLink activeClassName="is-active" to={massEditPath}>Mass Edit ({massEdit.numSelectedResponses})</NavLink>
    : <span style={{color: "#a2a1a1"}}>Mass Edit ({massEdit.numSelectedResponses})</span>;

  return (
    <div className="admin-container">
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <h4 className="title" dangerouslySetInnerHTML={{ __html: question.prompt }} />
        <h4 className="title" style={{color: '#00c2a2'}}>Flag: {question.flag}</h4>
      </div>
      <Cues
        displayArrowAndText={true}
        question={question}
      />
      <div className="feedback-row student-feedback-inner-container admin-feedback-row">
        <img alt="Directions Icon" className="info" src={icon} />
        <p>{question.instructions || 'Fill in the blank with the correct option.'}</p>
      </div>
      <p className="control button-group" style={{ marginTop: 10 }}>
        <Link className="button is-outlined is-primary" to={editPath}>Edit Question</Link>
      </p>
      <div className="tabs">
        <ul>
          <li><NavLink activeClassName="is-active" to={responsesPath}>Responses</NavLink></li>
          <li><NavLink activeClassName="is-active" to={testPath}>Play Question</NavLink></li>
          {activeLink && <li>{activeLink}</li>}
          {editBlankByBlankGrading && <li><NavLink activeClassName="is-active" to={gradingPath}>Grading</NavLink></li>}
        </ul>
      </div>
      <Switch>
        <Route
          path={gradingRoutePath}
          render={(routeProps) => (
            <BlankByBlankGradingPanel
              {...routeProps}
              editBlankByBlankGrading={editBlankByBlankGrading}
              question={question}
              questionID={questionID}
            />
          )}
        />
        <Route component={ResponseComponent} path={responsesRoutePath} />
        <Route
          path={massEditRoutePath}
          render={(routeProps) => (
            <MassEditContainer
              {...routeProps}
              actions={{
                clearDisplayMessage: actions.clearDisplayMessage,
                clearResponses: actions.clearResponses,
                deleteAll: actions.deleteAll,
                removeResponse: actions.removeResponse,
                submitConceptResults: actions.submitConceptResults,
                submitFeedback: actions.submitFeedback
              }}
              dispatch={dispatch}
              displayError={displayError}
              displayMessage={displayMessage}
              questionID={questionID}
              selectedResponses={massEdit.selectedResponses}
            />
          )}
        />
        <Route path={testRoutePath} render={TestComponent} />
        <Route
          path={editRoutePath}
          render={(routeProps) => {
            const handleClose = () => {
              routeProps.history.push(responsesPath);
            };

            const handleSubmit = (data: any) => {
              if (editQuestionAction) {
                editQuestionAction(questionID, data);
              }
              handleClose();
            };

            return (
              <Modal close={handleClose}>
                <EditFillInBlank
                  action={handleSubmit}
                  isGrammar={isGrammar}
                  questionState={{
                    blankAllowed: question.blankAllowed,
                    blankByBlankGrading: question.blankByBlankGrading,
                    caseInsensitive: question.caseInsensitive,
                    conceptID: question.conceptID,
                    cues: serializeCues(question.cues),
                    cuesLabel: question.cuesLabel,
                    flag: question.flag,
                    instructions: question.instructions,
                    prompt: question.prompt,
                    ruleDescription: question.ruleDescription
                  }}
                />
              </Modal>
            );
          }}
        />
      </Switch>
    </div>
  );
};

export const FillInBlankQuestion = withRouter(FillInBlankQuestionComponent);
